import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Camera, CheckCircle, Users, FileText, Settings, Search, BarChart3, Wrench, Plane, CornerDownLeft, Edit, Package, FolderOpen, Palmtree, MessageSquare, MapPin, Calculator, Clock, Trash2, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge.jsx";
import { useMenuConfig } from "@/hooks/useMenuConfig";

// Zentrale Actions-Liste mit Icons
export const ACTIONS = [
  {
    id: "checkout",
    label: "Checkout",
    icon: FileText,
    path: "/checkout",
    badge: null,
    color: "text-indigo-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "neue-bilder",
    label: "Neue Bilder ohne Exposé",
    icon: Camera,
    path: "/neue-bilder",
    badge: null, // Wird dynamisch gesetzt
    color: "text-orange-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "data-check",
    label: "Data Check",
    icon: CheckCircle,
    path: "/data-check",
    color: "text-green-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "benutzer",
    label: "Benutzer",
    icon: Users,
    path: "/users",
    color: "text-blue-600",
    roles: ["superadmin"] // Nur für Superadmin
  },
  {
    id: "audit-log",
    label: "Audit Log",
    icon: FileText,
    path: "/audit-log",
    color: "text-purple-600",
    roles: ["superadmin"] // Nur für Superadmin
  },
  {
    id: "auswertungen",
    label: "Auswertungen",
    icon: BarChart3,
    path: "/statistics",
    color: "text-teal-600",
    roles: ["superadmin"]
  },
  {
    id: "wartungen",
    label: "Wartungen",
    icon: Wrench,
    path: "/wartungen",
    color: "text-green-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "wartungen-karte",
    label: "Wartungen Karte",
    icon: MapPin,
    path: "/wartungen/karte",
    color: "text-blue-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "ruecklieferungen",
    label: "Rücklieferungen",
    icon: CornerDownLeft,
    path: "/ruecklieferungen",
    color: "text-orange-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "bauhof",
    label: "Bauhof Management",
    icon: Package,
    path: "/bauhof",
    color: "text-gray-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "batch-bearbeitung",
    label: "Vermietung und Verkauf",
    icon: Edit,
    path: "/batch-bearbeitung",
    color: "text-indigo-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "reisen",
    label: "Reisekosten",
    icon: Plane,
    path: "/reisen",
    color: "text-purple-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "urlaubsplaner",
    label: "Urlaubsplaner",
    icon: Palmtree,
    path: "/urlaubsplaner",
    color: "text-teal-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "media-verwaltung",
    label: "Media Verwaltung",
    icon: FolderOpen,
    path: "/media-verwaltung",
    color: "text-blue-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "anfragen",
    label: "Anfragen",
    icon: MessageSquare,
    path: "/anfragen",
    color: "text-yellow-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "kalkulation",
    label: "Kalkulation",
    icon: Calculator,
    path: "/kalkulation",
    color: "text-emerald-600",
    roles: ["superadmin", "backoffice"]
  },
  {
    id: "lebenslauf",
    label: "Lebenslauf",
    icon: History,
    path: "/lebenslauf",
    color: "text-rose-600",
    roles: ["superadmin", "backoffice", "user"]
  },
  {
    id: "einstellungen",
    label: "Einstellungen",
    icon: Settings,
    path: "/settings",
    color: "text-gray-600",
    roles: ["superadmin", "backoffice"]
  }
];

import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";

const TYP_ICON = {
  becken: Package,
  anlagen: Wrench,
  wartungen: Wrench,
  berichte: FileText,
  kunden: Users,
  designs: FolderOpen,
  cad: FileText,
};

const TYP_COLOR = {
  becken: "text-blue-600",
  anlagen: "text-indigo-600",
  wartungen: "text-green-600",
  berichte: "text-emerald-600",
  kunden: "text-amber-600",
  designs: "text-purple-600",
  cad: "text-cyan-700",
};

const RECENT_KEY = "mps-recent-searches";

const ladeLetzteSuchen = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw).slice(0, 8) : [];
  } catch (err) {
    return [];
  }
};

const merkeSuche = (q) => {
  const begriff = (q || "").trim();
  if (begriff.length < 2) return ladeLetzteSuchen();
  const liste = [begriff, ...ladeLetzteSuchen().filter((x) => x.toLowerCase() !== begriff.toLowerCase())].slice(0, 8);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(liste));
  } catch (err) {
    /* Speicher voll – Verlauf ist optional */
  }
  return liste;
};

export default function CommandPalette({ open, onClose, actionCounts = {} }) {
  const [search, setSearch] = useState("");
  const [gruppen, setGruppen] = useState([]);
  const [suchlauf, setSuchlauf] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [letzteSuchen, setLetzteSuchen] = useState(ladeLetzteSuchen);
  const navigate = useNavigate();
  const { enabledActions } = useMenuConfig();
  const { user } = useAuth();

  // Filtere nur aktivierte Actions basierend auf User-Konfiguration und Suche
  const filteredActions = ACTIONS.filter(action => {
    // Prüfe ob Action aktiviert ist
    const isEnabled = enabledActions.some(item => item.id === action.id);
    if (!isEnabled) return false;
    
    // Prüfe Rollen-Berechtigung
    if (action.roles && user?.role && !action.roles.includes(user.role)) {
      return false; // User hat keine Berechtigung
    }
    
    // Prüfe ob Action zur Suche passt
    return action.label.toLowerCase().includes(search.toLowerCase());
  });

  // Datenbank-Suche (Becken, Anlagen, Wartungen, Berichte, Kunden)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setGruppen([]);
      setSuchlauf(false);
      return;
    }
    setSuchlauf(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/global-search", { params: { q } });
        setGruppen(res.data.gruppen || []);
      } catch (err) {
        setGruppen([]);
      } finally {
        setSuchlauf(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setGruppen([]);
      setActiveIndex(0);
      setLetzteSuchen(ladeLetzteSuchen());
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [search]);

  // Flache Liste für Tastatursteuerung
  const verlaufSichtbar = search.trim().length < 2 && letzteSuchen.length > 0;
  const items = [
    ...(verlaufSichtbar ? letzteSuchen.map((begriff) => ({ kind: "recent", titel: begriff })) : []),
    ...filteredActions.map((a) => ({ kind: "action", action: a })),
    ...gruppen.flatMap((g) => g.treffer.map((t) => ({ kind: g.typ, gruppe: g.label, ...t }))),
  ];

  const oeffnen = (item) => {
    if (!item) return;
    if (item.kind === "recent") {
      setSearch(item.titel);
      return;
    }
    if (item.kind === "action") navigate(item.action.path);
    else {
      setLetzteSuchen(merkeSuche(search));
      if (item.url) window.open(item.url, "_blank", "noreferrer");
      else if (item.pfad) navigate(item.pfad);
    }
    onClose();
    setSearch("");
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onClose ? onClose() : null;
      }
      if (!open) return;
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, items.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter") {
        e.preventDefault();
        oeffnen(items[activeIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, items, activeIndex]);

  if (!open) return null;

  let laufIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        {/* Search Input */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Aktionen, Seriennummern, Wartungen, Kunden, Berichte..."
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
            data-testid="palette-search-input"
          />
          <kbd className="pointer-events-none ml-auto hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">ESC</span>
          </kbd>
        </div>

        {/* Treffer */}
        <div className="max-h-[500px] overflow-y-auto p-2">
          {items.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="palette-empty">
              {suchlauf ? "Suche läuft..." : search.trim().length < 2 ? "Mindestens 2 Zeichen eingeben." : "Keine Treffer gefunden."}
            </div>
          ) : (
            <div className="space-y-3">
              {verlaufSichtbar && (
                <div className="space-y-1" data-testid="palette-group-recent">
                  <div className="flex items-center justify-between px-3 pt-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Letzte Suchen</span>
                    <button
                      onClick={() => {
                        localStorage.removeItem(RECENT_KEY);
                        setLetzteSuchen([]);
                      }}
                      className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                      data-testid="palette-recent-clear"
                    >
                      <Trash2 className="h-3 w-3" /> Verlauf leeren
                    </button>
                  </div>
                  {letzteSuchen.map((begriff) => {
                    laufIndex += 1;
                    const idx = laufIndex;
                    return (
                      <button
                        key={begriff}
                        onClick={() => oeffnen({ kind: "recent", titel: begriff })}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                          activeIndex === idx ? "bg-accent" : "hover:bg-accent"
                        }`}
                        data-testid={`palette-item-${idx}`}
                      >
                        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="flex-1 truncate">{begriff}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {filteredActions.length > 0 && (
                <div className="space-y-1">
                  <div className="px-3 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Aktionen</div>
                  {filteredActions.map((action) => {
                    const Icon = action.icon;
                    const count = actionCounts[action.id];
                    laufIndex += 1;
                    const idx = laufIndex;
                    return (
                      <button
                        key={action.id}
                        onClick={() => oeffnen({ kind: "action", action })}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                          activeIndex === idx ? "bg-accent" : "hover:bg-accent"
                        }`}
                        data-testid={`palette-item-${idx}`}
                      >
                        <Icon className={`h-5 w-5 ${action.color}`} />
                        <span className="flex-1 font-medium">{action.label}</span>
                        {count > 0 && (
                          <Badge variant="secondary" className="ml-auto">
                            {count}
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {gruppen.map((gruppe) => {
                const Icon = TYP_ICON[gruppe.typ] || FileText;
                return (
                  <div key={gruppe.typ} className="space-y-1" data-testid={`palette-group-${gruppe.typ}`}>
                    <div className="px-3 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {gruppe.label}
                    </div>
                    {gruppe.treffer.map((treffer) => {
                      laufIndex += 1;
                      const idx = laufIndex;
                      return (
                        <button
                          key={`${gruppe.typ}-${treffer.id}`}
                          onClick={() => oeffnen({ kind: gruppe.typ, ...treffer })}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                            activeIndex === idx ? "bg-accent" : "hover:bg-accent"
                          }`}
                          data-testid={`palette-item-${idx}`}
                        >
                          <Icon className={`h-5 w-5 shrink-0 ${TYP_COLOR[gruppe.typ] || "text-neutral-500"}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{treffer.titel}</span>
                            {treffer.untertitel && (
                              <span className="block truncate text-xs text-muted-foreground">{treffer.untertitel}</span>
                            )}
                          </span>
                          {treffer.url && <Badge variant="secondary">{treffer.endung || "PDF"}</Badge>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Hint */}
        <div className="border-t px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>Tipp: ⌘K / Strg+K öffnet die Suche · ↑↓ wählen · Enter öffnen</span>
          {suchlauf && <span>Suche läuft...</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
